import React, { useState, useEffect, useCallback } from "react";
import { adminApiRequest } from "../utils/adminApi";
import Modal from "./Modal";
import PageLoader from "./PageLoader";
import placeholderImage from "../img/placeholder-img.png";

const Soldout = () => {
  const [itemToInstock, setItemToInstock] = useState(null);
  const [showInstockModal, setShowInstockModal] = useState(false);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [error, setError] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const itemsPerPage = 50;
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const searchResults = filteredItems.filter((item) =>
    item.item_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const adjustedPage = currentPage > totalPages ? 1 : currentPage;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchResults.slice(indexOfFirstItem, indexOfLastItem);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState("amount");
  const [adjustmentDirection, setAdjustmentDirection] = useState("increase");
  const [adjustmentValue, setAdjustmentValue] = useState("");
  const [showPriceAdjustment, setShowPriceAdjustment] = useState(false);
  const [skippedItems, setSkippedItems] = useState([]);
  const [showMultipleInstockModal, setShowMultipleInstockModal] =
    useState(false);
  const [showMultipleAdjustPriceModal, setShowMultipleAdjustPriceModal] =
    useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [showSkippedItemsModal, setShowSkippedItemsModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {}, [filteredItems]);

  const fetchMenuItems = useCallback(async (filterValue) => {
    setLoadingMessage("Fetching menu items...");
    setIsSubmitting(true);
    try {
      const res = await adminApiRequest({
        script: "fetch-soldout-items",
        data: {},
      });

      if (!res?.success || !Array.isArray(res.data)) {
        throw new Error(res?.error || res?.message || "Invalid response data");
      }

      setMenuItems(res.data);

      if (filterValue && filterValue !== "all") {
        const selectedCategoryId = parseInt(filterValue, 10);
        const filtered = res.data.filter(
          (item) => Number(item.category_id) === selectedCategoryId,
        );
        setFilteredItems(filtered);
      } else {
        setFilteredItems(res.data);
      }
    } catch (err) {
      setMenuItems([]);
      setFilteredItems([]);
      setErrorWithTimeout(err?.message || "Error fetching soldout items");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuItems(selectedFilter);
  }, [selectedFilter, fetchMenuItems]);

  const fetchAllCategories = useCallback(async () => {
    setLoadingMessage("Fetching categories...");
    setIsSubmitting(true);
    try {
      const res = await adminApiRequest({
        script: "categories",
        data: {},
      });

      setCategories(res?.success ? res.data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const handleCloseModal = () => {
    setShowInstockModal(false);
    setShowDeleteItemModal(false);
    setShowMultipleInstockModal(false);
    setShowMultipleDeleteModal(false);
    setShowMultipleAdjustPriceModal(false);
  };

  const handleMarkAsInstock = (item) => {
    setItemToInstock(item);
    setShowInstockModal(true);
  };

  useEffect(() => {
    fetchAllCategories();
  }, [fetchAllCategories]);

  const confirmMarkAsInstock = async (e) => {
    e.preventDefault();
    setLoadingMessage("Marking item as available...");
    setIsSubmitting(true);

    if (!itemToInstock?.id) {
      setErrorWithTimeout("Invalid item selected for marking as available");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await adminApiRequest({
        script: "soldout-items",
        data: {
          id: itemToInstock.id,
          in_stock: 1,
        },
      });

      if (res?.success) {
        showSuccess(res?.message || "Item marked as available");
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Error marking item as available",
        );
      }
    } catch (err) {
      setErrorWithTimeout(err?.message || "Error marking item as available");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setShowInstockModal(false);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setShowDeleteItemModal(true);
  };

  const confirmDeleteItem = async (e) => {
    e.preventDefault();
    setLoadingMessage("Deleting item...");
    setIsSubmitting(true);

    if (!itemToDelete?.id) {
      setErrorWithTimeout("Invalid item selected for deletion");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await adminApiRequest({
        script: "delete-item",
        data: { id: itemToDelete.id },
      });

      if (res?.success) {
        showSuccess(res?.message || "Item deleted successfully");
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Error deleting item",
        );
      }
    } catch (err) {
      setErrorWithTimeout(err?.message || "Error deleting item");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        handleCloseModal();
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const setErrorWithTimeout = (errorMessage) => {
    setError(errorMessage);
    if (errorMessage) {
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const toggleBulkEdit = () => {
    setShowBulkEdit(!showBulkEdit);
    setShowPriceAdjustment(false);
    setSelectedItems([]);
  };

  const handleFilterChange = (selectedCategory) => {
    setSelectedFilter(selectedCategory);
    setCurrentPage(1);
    if (selectedCategory === "all") {
      setFilteredItems(menuItems);
    } else {
      const selectedCategoryId = parseInt(selectedCategory);
      const filtered = menuItems.filter(
        (item) => Number(item.category_id) === selectedCategoryId,
      );
      setFilteredItems(filtered);
    }
  };

  const handleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(currentItems.map((item) => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const confirmPriceAdjustment = async (e) => {
    e.preventDefault();
    setLoadingMessage("Adjusting prices...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "adjust-prices",
        data: {
          selectedItems,
          adjustmentType,
          adjustmentDirection,
          adjustmentValue: parseFloat(adjustmentValue),
        },
      });

      if (res?.success) {
        showSuccess(res.message || "Prices adjusted successfully");

        if (Array.isArray(res.skippedItems) && res.skippedItems.length > 0) {
          setSkippedItems(res.skippedItems);
          setShowSkippedItemsModal(true);
        }
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Failed to adjust prices",
        );
      }
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while adjusting prices",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setSelectedItems([]);
        setAdjustmentType("amount");
        setAdjustmentDirection("increase");
        setAdjustmentValue("");
        setShowMultipleAdjustPriceModal(false);
        togglePriceAdjustment(false);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const togglePriceAdjustment = () => {
    if (showPriceAdjustment) {
      setAdjustmentType("amount");
      setAdjustmentDirection("increase");
      setAdjustmentValue("");
    }
    setShowPriceAdjustment(!showPriceAdjustment);
  };

  const confirmMarkSelectedAsInstock = async (e) => {
    e.preventDefault();
    setLoadingMessage("Marking selected items as in stock...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "multiple-soldout-items",
        data: {
          items: selectedItems.map((id) => ({ id, in_stock: 1 })),
        },
      });

      if (res?.success) {
        showSuccess(res?.message || "Items marked as in stock");
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Failed to mark items as in stock",
        );
      }
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while marking items as in stock",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setShowMultipleInstockModal(false);
        setSelectedItems([]);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const confirmDeleteSelectedItems = async (e) => {
    e.preventDefault();
    setLoadingMessage("Deleting selected items...");
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "delete-multiple-items",
        data: { ids: selectedItems },
      });

      if (res?.success) {
        showSuccess(
          res?.message ||
            `${selectedItems.length} item(s) deleted successfully`,
        );
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Failed to delete selected items",
        );
      }
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while deleting items",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        handleCloseModal();
        setSelectedItems([]);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const priority = {
      1: 1,
      17: 2,
      16: 99,
    };

    const aPriority = priority[a.id] || 50;
    const bPriority = priority[b.id] || 50;

    return aPriority - bPriority;
  });

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  return (
    <main className="h-full overflow-y-auto">
      {showSuccessMessage && (
        <div className="fixed right-4 top-4 z-50 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 shadow-lg">
          {successMessage}
        </div>
      )}

      <div className="w-full">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-4 md:px-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                  Sold Out Items
                </h1>
              </div>

              <div className="flex w-full flex-col gap-3 md:flex-row xl:w-auto">
                <div className="w-full sm:min-w-[220px]">
                  <select
                    id="kid-status-filter"
                    className={inputClass}
                    value={selectedFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    <option value="all">All Items</option>

                    {(sortedCategories || []).map((category) => (
                      <React.Fragment key={category.id}>
                        <option value={category.id}>
                          {category.category_name}
                        </option>

                        {(category.subcategories || []).map((subcat) => (
                          <option key={subcat.id} value={subcat.id}>
                            - {subcat.category_name}
                          </option>
                        ))}
                      </React.Fragment>
                    ))}
                  </select>
                </div>

                <div className="w-full xl:w-[320px]">
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={inputClass}
                  />
                </div>

                <button
                  onClick={toggleBulkEdit}
                  className={`inline-flex shrink-0 whitespace-nowrap items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold transition ${
                    showBulkEdit
                      ? "bg-rose-600 text-white hover:bg-rose-700"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {showBulkEdit ? "Close Bulk Edit" : "Bulk Edit"}
                </button>
              </div>
            </div>
          </div>

          {showBulkEdit && (
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-4 md:px-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedItems.length === currentItems.length &&
                          currentItems.length > 0
                        }
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span className="font-medium">Select All</span>
                    </label>

                    <span className="text-sm font-medium text-slate-600">
                      {selectedItems.length} item(s) selected
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        setShowMultipleInstockModal(true);
                      }}
                    >
                      Mark as In Stock
                    </button>

                    <button
                      className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        setShowMultipleDeleteModal(true);
                      }}
                    >
                      Delete Selected
                    </button>

                    <button
                      className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        togglePriceAdjustment(true);
                      }}
                    >
                      Adjust Price
                    </button>
                  </div>
                </div>

                {showPriceAdjustment && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-4">
                      <p className="text-sm font-semibold text-slate-900">
                        Price Adjustment
                      </p>

                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentDirection"
                            value="increase"
                            checked={adjustmentDirection === "increase"}
                            onChange={(e) =>
                              setAdjustmentDirection(e.target.value)
                            }
                          />
                          Increase
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentDirection"
                            value="decrease"
                            checked={adjustmentDirection === "decrease"}
                            onChange={(e) =>
                              setAdjustmentDirection(e.target.value)
                            }
                          />
                          Decrease
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentType"
                            value="amount"
                            checked={adjustmentType === "amount"}
                            onChange={(e) => setAdjustmentType(e.target.value)}
                          />
                          By Amount
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentType"
                            value="percentage"
                            checked={adjustmentType === "percentage"}
                            onChange={(e) => setAdjustmentType(e.target.value)}
                          />
                          By Percentage
                        </label>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder={`Enter ${
                              adjustmentType === "amount"
                                ? "Amount"
                                : "Percentage"
                            }`}
                            value={adjustmentValue}
                            onChange={(e) => setAdjustmentValue(e.target.value)}
                            className={`${inputClass} pr-12`}
                          />
                          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                            {adjustmentType === "amount" ? "₦" : "%"}
                          </span>
                        </div>

                        <button
                          className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                          onClick={() => {
                            setShowMultipleAdjustPriceModal(true);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-4 md:p-6">
            {currentItems.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
                No soldout items found.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {currentItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-3">
                          {showBulkEdit && (
                            <input
                              type="checkbox"
                              className="mt-3 h-4 w-4 rounded border-slate-300"
                              checked={selectedItems.includes(item.id)}
                              onChange={() => handleSelect(item.id)}
                            />
                          )}

                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
                            {(adjustedPage - 1) * itemsPerPage + index + 1}
                          </div>

                          {item.image ? (
                            <img
                              src={`https://deedelicious.com/${item.image}`}
                              alt={item.item_name}
                              className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 object-cover"
                            />
                          ) : (
                            <img
                              src={placeholderImage}
                              alt="Placeholder"
                              className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 object-cover"
                            />
                          )}

                          <div className="min-w-0 flex-1 max-[479px]:hidden">
                            <p className="break-words text-sm font-semibold leading-5 text-slate-900 md:text-base">
                              {item.item_name}
                            </p>

                            {item.price !== "0.00" && (
                              <p className="mt-1 text-sm font-medium text-slate-500">
                                £{item.price}
                              </p>
                            )}


                          </div>
                        </div>

                        <div className="mt-3 hidden min-w-0 max-[479px]:block">
                          <p className="break-words text-sm font-semibold leading-5 text-slate-900">
                            {item.item_name}
                          </p>

                          {item.price !== "0.00" && (
                            <p className="mt-1 text-sm font-medium text-slate-500">
                              £{item.price}
                            </p>
                          )}


                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 xl:flex-nowrap xl:shrink-0">
                        <button title="Mark Complete"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 text-emerald-600 transition hover:bg-emerald-50"
                          onClick={() => handleMarkAsInstock(item)}
                        >
                          <i className="fas fa-check-double text-sm"></i>
                        </button>

                        <button title="Delete"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                          onClick={() => handleDeleteItem(item)}
                        >
                          <i className="fas fa-trash-alt text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`min-w-[42px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      adjustedPage === index + 1
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <PageLoader message={loadingMessage} />
      )}

      {showDeleteItemModal && (
        <Modal
          title="Delete Item"
          onClose={() => handleCloseModal()}
          onSubmit={confirmDeleteItem}
          submitButtonText="Delete"
          showCancel={true}
          showSubmitButton={true}
          className="modal-content delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to delete this item?
            </p>
          </div>
        </Modal>
      )}

      {showInstockModal && (
        <Modal
          title="Mark Item as Available"
          onClose={handleCloseModal}
          onSubmit={confirmMarkAsInstock}
          submitButtonText="Mark as Available"
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
            <p className="text-sm font-medium text-emerald-700">
              Are you sure you want to mark this item as available?
            </p>
          </div>
        </Modal>
      )}

      {showMultipleInstockModal && (
        <Modal
          title="Mark Selected Items as In Stock"
          onClose={handleCloseModal}
          onSubmit={confirmMarkSelectedAsInstock}
          submitButtonText="Mark as In Stock"
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
            <p className="text-sm font-medium text-emerald-700">
              Are you sure you want to mark the selected items as in stock?
            </p>
          </div>
        </Modal>
      )}

      {showSkippedItemsModal && (
        <Modal
          title="Price Adjustment Successful"
          onClose={() => setShowSkippedItemsModal(false)}
          showCancel={false}
          showSubmitButton={false}
          className="skipped-items-modal"
        >
          <div className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Prices adjusted successfully, but the following items could not be
              adjusted to avoid negative prices:
            </p>

            <ul className="list-disc space-y-1 pl-5 text-sm text-amber-700">
              {skippedItems.map((itemName, index) => (
                <li key={index}>{itemName}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      {showMultipleDeleteModal && (
        <Modal
          title="Delete Items"
          onClose={handleCloseModal}
          onSubmit={confirmDeleteSelectedItems}
          submitButtonText="Delete Items"
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to permanently delete the selected items
              from the database?
            </p>
          </div>
        </Modal>
      )}

      {showMultipleAdjustPriceModal && (
        <Modal
          title="Adjust Prices"
          onClose={handleCloseModal}
          onSubmit={confirmPriceAdjustment}
          submitButtonText="Adjust Prices"
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
        >
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Are you sure you want to adjust the prices of the selected items?
            </p>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default Soldout;
